import { TZ_OFFSET_S } from "../limits.ts";
import { en } from "./en.ts";
import { vi, type MessageKey } from "./vi.ts";

export type Locale = "vi" | "en";
export type { MessageKey };

export const messages: Record<Locale, Record<MessageKey, string>> = { vi, en };

export type Params = Record<string, string | number>;

/** Renders a message with {name} placeholders. Missing params are left visible. */
export function t(locale: Locale, key: MessageKey, params?: Params): string {
  const template = messages[locale][key] ?? messages.vi[key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m,
  );
}

const vnd = {
  vi: new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }),
  en: new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }),
};

/** 12000000 → "12.000.000 ₫" in both locales (VND is always shown the Vietnamese way). */
export function formatVnd(amount: number, locale: Locale = "vi"): string {
  return vnd[locale].format(amount);
}

/** Digits only with dot grouping, for the big ticking price: "12.000.000". */
export function formatVndDigits(amount: number): string {
  return new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 }).format(amount);
}

export function pickLocale(input: string | null | undefined): Locale {
  return input?.toLowerCase().startsWith("en") ? "en" : "vi";
}

/** Local (UTC+7) wall-clock parts of a unix-seconds instant. */
export function localParts(ts: number): {
  y: number;
  m: number;
  d: number;
  hh: number;
  mm: number;
  dow: number;
} {
  const local = new Date((ts + TZ_OFFSET_S) * 1000);
  return {
    y: local.getUTCFullYear(),
    m: local.getUTCMonth() + 1,
    d: local.getUTCDate(),
    hh: local.getUTCHours(),
    mm: local.getUTCMinutes(),
    dow: local.getUTCDay(),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "T7 20:00" / "Sat 20:00" for a Drop instant; with date when not within 7 days: "T7 12/09 20:00". */
export function formatDropTime(ts: number, locale: Locale, now?: number): string {
  const p = localParts(ts);
  const dow =
    p.dow === 6
      ? t(locale, "time.weekday.6")
      : p.dow === 0
        ? t(locale, "time.weekday.0")
        : `${p.d}/${pad(p.m)}`;
  const hhmm = `${pad(p.hh)}:${pad(p.mm)}`;
  if (now !== undefined && ts - now >= 7 * 86_400) return `${dow} ${pad(p.d)}/${pad(p.m)} ${hhmm}`;
  return `${dow} ${hhmm}`;
}

/** "2n 03g 12p" / "2d 03h 12m" countdown to a future instant. */
export function formatCountdown(seconds: number, locale: Locale): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  return t(locale, "drop.countdown", { d, h: pad(h), m: pad(m) });
}

/** "2026-09-12" in Asia/Ho_Chi_Minh for a unix instant (used by listing_views_daily). */
export function localDay(ts: number): string {
  const p = localParts(ts);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

export function formatRelative(ts: number, now: number, locale: Locale): string {
  const diff = Math.max(0, now - ts);
  if (diff < 60) return t(locale, "time.now");
  if (diff < 3_600) return t(locale, "time.minutes_ago", { n: Math.floor(diff / 60) });
  if (diff < 86_400) return t(locale, "time.hours_ago", { n: Math.floor(diff / 3_600) });
  return t(locale, "time.days_ago", { n: Math.floor(diff / 86_400) });
}
